import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Search, Sparkles, Wand2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import AILabShell from "@/components/ai-lab/AILabShell";
import CategoryIcon, { OUTPUT_TYPE_ICON } from "@/components/ai-lab/CategoryIcon";
import BrandProfileBanner from "@/components/ai-lab/BrandProfileBanner";
import FieldRenderer from "@/components/ai-lab/FieldRenderer";
import PromptOutput from "@/components/ai-lab/PromptOutput";
import TaskCard from "@/components/ai-lab/TaskCard";
import { CATEGORIES, CATEGORY_BY_ID, ACCENT_CLASSES } from "@/data/ai-lab/categories";
import { PROMPT_TASKS, TASK_BY_ID, tasksForCategory, searchTasks } from "@/data/ai-lab/promptLibrary";
import { renderTemplate, buildTaskData, validateTask, hasValue } from "@/lib/ai-lab/renderTemplate";
import { useBrandProfile } from "@/hooks/useBrandProfile";
import { eventsApi } from "@/lib/ai-lab/api";

/**
 * PromptBuilder — routes:
 *   /ai-cpg-tools/prompt-builder                 → pick a category, then a task (hierarchical)
 *   /ai-cpg-tools/prompt-builder?category=retail → category pre-selected
 *   /ai-cpg-tools/prompt-builder/:taskId         → the form + generated prompt
 */
export default function PromptBuilder() {
  const { taskId } = useParams();
  if (taskId) return <TaskBuilder taskId={taskId} />;
  return <TaskPicker />;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Step 1 + 2: hierarchical picker                                            */
/* ────────────────────────────────────────────────────────────────────────── */
function TaskPicker() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("category");
  const [query, setQuery] = useState("");
  const results = useMemo(() => (query.trim() ? searchTasks(query) : []), [query]);
  const category = selected ? CATEGORY_BY_ID[selected] : null;

  const choose = (id) => {
    setQuery("");
    setParams(id ? { category: id } : {});
  };

  return (
    <AILabShell
      crumbs={[{ label: "Prompt Builder", to: category ? "/ai-cpg-tools/prompt-builder" : undefined }, ...(category ? [{ label: category.short }] : [])]}
      title={category ? category.name : "Prompt Builder"}
      subtitle={category ? category.tagline : "What do you want to create? Pick a category, then a specific task. Answer a few questions and we'll write the expert prompt for you."}
      actions={category && <Button asChild variant="outline"><Link to={`/ai-cpg-tools/${category.slug}`}>Open {category.short} toolkit</Link></Button>}
    >
      <div className="mb-6"><BrandProfileBanner compact /></div>

      <div className="relative mb-6">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search all ${PROMPT_TASKS.length} prompts… e.g. "sell sheet", "logo", "Meta ad"`} className="h-11 pl-9 pr-9" />
        {query && <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search"><X className="h-4 w-4" /></button>}
      </div>

      {query.trim() ? (
        <section>
          <p className="mb-3 text-sm text-muted-foreground">{results.length} result{results.length === 1 ? "" : "s"} for “{query}”</p>
          {results.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="card-grid-auto gap-3">{results.map((t) => <TaskCard key={t.id} task={t} showCategory />)}</div>
          )}
        </section>
      ) : !category ? (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Step 1 · Choose a category</h2>
          <div className="card-grid-auto gap-3">
            {CATEGORIES.map((cat) => (
              <button key={cat.id} type="button" onClick={() => choose(cat.id)} className="group rounded-xl text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Card className={cn("h-full border transition-transform group-hover:-translate-y-0.5", ACCENT_CLASSES[cat.accent]?.tile)}>
                  <CardContent className="flex h-full flex-col gap-3 p-4">
                    <CategoryIcon icon={cat.icon} accent={cat.accent} />
                    <div>
                      <h3 className="font-semibold leading-tight text-foreground">{cat.short}</h3>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{cat.tagline}</p>
                    </div>
                    <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium">{tasksForCategory(cat.id).length} tasks <ArrowRight className="h-3.5 w-3.5" /></span>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Step 2 · Choose what to create</h2>
            <Button variant="ghost" size="sm" onClick={() => choose(null)}><ArrowLeft className="mr-1 h-4 w-4" /> All categories</Button>
          </div>
          <div className="card-grid-auto gap-3">
            {tasksForCategory(category.id).map((t) => <TaskCard key={t.id} task={t} />)}
          </div>
        </section>
      )}
    </AILabShell>
  );
}

function EmptyState() {
  return (
    <Card className="border-dashed">
      <CardContent className="p-8 text-center text-sm text-muted-foreground">
        No prompt matches that yet. Try a broader word (“packaging”, “buyer”, “video”) or browse by category.
      </CardContent>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Step 3: the form + output                                                  */
/* ────────────────────────────────────────────────────────────────────────── */
function TaskBuilder({ taskId }) {
  const navigate = useNavigate();
  const task = TASK_BY_ID[taskId];
  const { profile, hasProfile } = useBrandProfile();
  const [useProfile, setUseProfile] = useState(true);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [generated, setGenerated] = useState(null);
  const OutIcon = task ? OUTPUT_TYPE_ICON[task.outputType] || OUTPUT_TYPE_ICON.text : null;

  // Reset when navigating between tasks
  useEffect(() => {
    setValues({});
    setErrors({});
    setGenerated(null);
    if (task) eventsApi.track({ type: "view_task", taskId: task.id });
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeProfile = useProfile && hasProfile ? profile : null;
  const fromProfile = useMemo(() => {
    const set = new Set();
    if (!task || !activeProfile) return set;
    for (const f of task.fields) if (!hasValue(values[f.id]) && f.brandKey && hasValue(activeProfile[f.brandKey])) set.add(f.id);
    return set;
  }, [task, activeProfile, values]);

  if (!task) {
    return (
      <AILabShell crumbs={[{ label: "Prompt Builder", to: "/ai-cpg-tools/prompt-builder" }, { label: "Not found" }]} title="Prompt not found">
        <p className="text-sm text-muted-foreground">That prompt doesn't exist (yet). <Link className="underline" to="/ai-cpg-tools/prompt-builder">Browse all prompts</Link>.</p>
      </AILabShell>
    );
  }

  const category = CATEGORY_BY_ID[task.category];
  const data = buildTaskData(task, values, activeProfile);
  const preview = renderTemplate(task.template, data);

  const setField = (id, v) => {
    setValues((prev) => ({ ...prev, [id]: v }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: undefined }));
    if (generated !== null) setGenerated(null); // answers changed → back to preview mode
  };

  const generate = () => {
    const errs = validateTask(task, data);
    if (errs.length) {
      setErrors(Object.fromEntries(errs.map((e) => [e.fieldId, e.message])));
      toast.error(`Please fill in: ${errs.map((e) => e.message.replace(" is required", "")).join(", ")}`);
      document.getElementById(errs[0].fieldId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setGenerated(preview);
    eventsApi.track({ type: "generate_prompt", taskId: task.id, usedBrandProfile: !!activeProfile });
    if (window.innerWidth < 1024) setTimeout(() => document.getElementById("prompt-output")?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const siblings = tasksForCategory(task.category).filter((t) => t.id !== task.id).slice(0, 3);

  return (
    <AILabShell
      wide
      crumbs={[{ label: "Prompt Builder", to: "/ai-cpg-tools/prompt-builder" }, { label: category.short, to: `/ai-cpg-tools/prompt-builder?category=${category.id}` }, { label: task.title }]}
      title={task.title}
      subtitle={task.description}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="gap-1 font-normal"><OutIcon className="h-3 w-3" /> {task.outputType === "text" ? "Text prompt" : `${task.outputType[0].toUpperCase()}${task.outputType.slice(1)} prompt`}</Badge>
          <Badge variant="outline" className="gap-1 font-normal"><Clock className="h-3 w-3" /> {task.estimatedTime}</Badge>
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        {/* Left: form */}
        <div className="space-y-4">
          {hasProfile ? (
            <div className={cn("flex items-center justify-between gap-3 rounded-xl border p-3", useProfile ? "border-violet-300 bg-violet-50/60 dark:border-violet-800 dark:bg-violet-950/30" : "bg-card")}>
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className={cn("h-4 w-4", useProfile ? "text-violet-600" : "text-muted-foreground")} />
                <span>{useProfile ? <>Using <strong>{profile.brandName}</strong> brand profile — {fromProfile.size} field{fromProfile.size === 1 ? "" : "s"} filled</> : "Brand profile is off"}</span>
              </div>
              <Button type="button" size="sm" variant={useProfile ? "outline" : "default"} onClick={() => setUseProfile((v) => !v)}>
                {useProfile ? "Turn off" : "Use my brand profile"}
              </Button>
            </div>
          ) : (
            <BrandProfileBanner compact />
          )}

          <Card>
            <CardContent className="p-4 sm:p-5">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Tell us about it</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {task.fields.map((field) => (
                  <FieldRenderer
                    key={field.id}
                    field={field}
                    value={values[field.id] ?? (fromProfile.has(field.id) ? String(activeProfile[field.brandKey]) : field.default ?? "")}
                    onChange={(v) => setField(field.id, v)}
                    error={errors[field.id]}
                    fromProfile={fromProfile.has(field.id)}
                  />
                ))}
              </div>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">Fields marked * are required. Everything else makes the prompt sharper.</p>
                <Button size="lg" onClick={generate} className="bg-violet-600 hover:bg-violet-700"><Wand2 className="mr-2 h-4 w-4" /> Generate prompt</Button>
              </div>
            </CardContent>
          </Card>

          {siblings.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">More in {category.short}</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {siblings.map((t) => (
                  <Button key={t.id} variant="outline" className="h-auto justify-start whitespace-normal py-2 text-left text-xs" onClick={() => navigate(`/ai-cpg-tools/prompt-builder/${t.id}`)}>{t.title}</Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: output */}
        <div id="prompt-output" className="lg:sticky lg:top-4 lg:self-start">
          {generated !== null ? (
            <PromptOutput task={task} prompt={generated} formValues={data} onReset={() => setGenerated(null)} />
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-4 sm:p-5">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Live preview</h2>
                  <span className="text-xs text-muted-foreground">Updates as you type</span>
                </div>
                <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg bg-muted/50 p-4 font-mono text-[12.5px] leading-relaxed text-muted-foreground">{preview}</pre>
                <p className="mt-3 text-xs text-muted-foreground">Click <strong>Generate prompt</strong> to lock it in, copy it, and open your recommended AI tool.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AILabShell>
  );
}
